import camelCase from 'lodash/camelCase';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import isNil from 'lodash/isNil';
import partition from 'lodash/partition';
import _values from 'lodash/values';
import { action, extendObservable } from 'mobx';
import { UiService } from 'modules/ui';
import { UserManagerService } from 'modules/userManager';
import 'modules/userManager/UserManagerApi';

import {
  contentFilterFields,
  initialFilters,
  initialFiltersValue,
  initialState,
  menus,
  userFilterFields
} from './constants';
import Store from './DeepSearchStore';

import Request from '@hotmart/request';

const customFilters = {
  contentFilter: [
    contentFilterFields.userProgress,
    contentFilterFields.page,
    contentFilterFields.quizScore
  ],
  userFilter: [userFilterFields.userEngagement]
};

const menuSelectedInitial = {
  content: false,
  access: false,
  user: false
};

class DeepSearchService {
  // #region setters
  constructor() {
    this.clearFilters();
  }

  @action
  clear() {
    this.setData({ ...initialState });
  }

  @action
  clearFilters() {
    this.setData({ deepSearch: this.emptySearch, isDirty: true });
  }

  @action
  setData(data = {}) {
    extendObservable(Store, { ...data });
  }

  async setDeepSearch(deepSearch) {
    await this.resetMenuSelected();
    this.setData({ deepSearch, isDirty: false });
    this.setMenuSelected();
  }

  clearFiltersByCallback = (onClearFilters) => {
    this.clearFilters();

    const clearFunctions = _values(Store.clearFunctions);

    clearFunctions.forEach((clearFunction) => clearFunction());

    onClearFilters && onClearFilters();
  };

  setClearFunctionReference = (key, clearFunction) =>
    (Store.clearFunctions[key] = clearFunction);

  unsetClearFunctionReference = (key) => delete Store.clearFunctions[key];

  setDeepSearchFilter(categoryName, filterName, value) {
    const category = Store.deepSearch[categoryName];
    const deepSearch = Object.assign({}, Store.deepSearch, {
      [categoryName]: {
        ...category,
        [filterName]: { ...category[filterName], ...value }
      }
    });

    this.setData({ deepSearch });
  }

  @action
  setMenuSelected(menu = menus.content) {
    this.setData({ menuSelected: { ...menuSelectedInitial, [menu]: true } });
  }

  @action
  resetMenuSelected() {
    this.setData({ menuSelected: { ...menuSelectedInitial } });
  }

  setSearchParameters(search) {
    const deepSearch = this.emptySearch;
    const existingFilters = this.existingFilters(search.filters);
    const [customFilters, commonFilters] = partition(
      Object.keys(existingFilters),
      (filterName) => this.isCustomFilter(filterName)
    );

    if (commonFilters.length) {
      Object.keys(initialFilters).forEach((categoryName) => {
        Object.keys(deepSearch[categoryName])
          .filter((filterName) => commonFilters.includes(filterName))
          .forEach((filterName) => {
            const filterValue = existingFilters[filterName][0];

            this.setFilter(deepSearch, categoryName, filterName, filterValue);
          });
      });
    }

    if (customFilters.length) {
      const customFiltersSubset = customFilters.reduce((acc, filterName) => {
        acc[filterName] = existingFilters[filterName];
        return acc;
      }, {});

      this.setCustomFilters(deepSearch, customFiltersSubset);
    }

    this.setDeepSearch(deepSearch);
  }

  setFilter(
    deepSearch,
    categoryName,
    filterName,
    filterValue,
    increment = true
  ) {
    Object.assign(deepSearch[categoryName][filterName], filterValue);

    if (increment) {
      this.incrementFilter(deepSearch, categoryName);
      this.incrementFilter(deepSearch, 'totalFilter');
    }
  }

  setCustomFilters(deepSearch, customFiltersSubset) {
    Object.keys(customFilters).forEach((categoryName) => {
      customFilters[categoryName]
        .filter((filterName) => filterName in customFiltersSubset)
        .forEach((filterName) => {
          const filterGroup = customFiltersSubset[filterName];

          switch (filterName) {
            case contentFilterFields.userProgress:
              this.setFilter(deepSearch, categoryName, filterName, {
                ...filterGroup[0],
                queryData: false
              });
              break;
            case contentFilterFields.page:
              this.setPageCustomFilter(
                filterGroup,
                deepSearch,
                categoryName,
                filterName
              );
              break;
            case contentFilterFields.quizScore:
              const ids = filterGroup.map((f) => f.id);
              const incrementCounterOnUpdate = !ids.length;
              const { id: _id, ...firstFilter } = filterGroup[0];
              const filterValue = {
                ...firstFilter,
                ids,
                queryData: incrementCounterOnUpdate
              };

              this.setFilter(deepSearch, categoryName, filterName, filterValue);
              break;
            case userFilterFields.userEngagement:
              this.setFilter(deepSearch, categoryName, filterName, {
                ...filterGroup[0],
                queryData: true
              });
              break;
            default:
          }
        });
    });
  }

  setPageCustomFilter(filterGroup, deepSearch, categoryName, filterName) {
    const groupedPages = partition(filterGroup, 'completed').map(
      (group) => group[0]
    );
    const [completedPagesFilter, incompletePagesFilter] = groupedPages;

    completedPagesFilter &&
      this.setFilter(
        deepSearch,
        categoryName,
        filterName,
        completedPagesFilter
      );
    incompletePagesFilter &&
      this.setFilter(
        deepSearch,
        categoryName,
        contentFilterFields.incompletePage,
        incompletePagesFilter,
        !completedPagesFilter
      );
  }

  existingFilters = (searchFilters) =>
    searchFilters.reduce((acc, filter) => {
      const filterType = camelCase(filter._type);

      if (!(filterType in acc)) {
        acc[filterType] = [];
      }

      acc[filterType].push(filter);

      return acc;
    }, {});

  isCustomFilter = (filterName) =>
    flatten(Object.values(customFilters)).includes(filterName);

  incrementFilter = (deepSearch, category) => deepSearch[`${category}Count`]++;

  cleanSimpleFilter() {
    const { simpleFilterParams } = Store;

    simpleFilterParams.query = '';

    this.setData({ simpleFilterParams });
  }

  setSearchType(isDeepSearch = false) {
    this.setData({ isDeepSearch });
  }

  setSimpleSearchQuery(query) {
    Object.assign(Store.simpleFilterParams, { query });
  }

  saveSearchParameters(lastSearchParameters) {
    this.setData({ lastSearchParameters });
  }

  openDeepSearch() {
    if (!Store.deepSearchOpened) {
      this.setData({ deepSearchOpened: true });
    }
  }

  closeDeepSearch() {
    if (Store.deepSearchOpened) {
      this.setData({ deepSearchOpened: false });
    }
  }

  setSearchLoading(loadingSearch = false) {
    this.setData({ loadingSearch });
  }

  setDeepSearchLoading(deepSearchLoading) {
    this.setData({ deepSearchLoading });
  }

  setSaveLoading(saveLoading = false) {
    this.setData({ saveLoading });
  }

  setDone(doneAttr, scroll) {
    if (scroll) {
      const headerOffset = 54;
      const marginOffset = 17;
      const offsetTop = headerOffset + marginOffset;

      UiService.smoothScroll(
        '.big-numbers__section__container',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        500,
        offsetTop
      );
    }

    this.setData({ [doneAttr]: true });

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    setTimeout(() => this.setData({ [doneAttr]: false }), 1000);
  }

  setDirty(isDirty) {
    if (!isNil(isDirty) && Store.isDirty !== isDirty) {
      this.setData({ isDirty });
    }
  }

  enableErrorFields() {
    this.setData({ error: true });
  }

  disableErrorFields() {
    this.setData({ error: false });
  }

  validateDeepSearch() {
    const isValid = !this.errorFields;

    isValid ? this.disableErrorFields() : this.enableErrorFields();

    return isValid;
  }

  async searchUsers() {
    this.setSearchLoading(true);

    if (Store.errorFields) {
      this.enableErrorFields();
    } else {
      UserManagerService.setData({ page: 0 });
      await UserManagerService.searchUser({ selectAll: false });

      this.disableErrorFields();
    }

    this.setSearchLoading(false);
  }

  async deepSearch({
    searchUsers = this.searchUsers.bind(this),
    scroll = true,
    showLoading = true
  }) {
    showLoading && this.setDeepSearchLoading(true);

    this.setSearchType(true);
    this.setSimpleSearchQuery('');

    await searchUsers();

    if (showLoading) {
      this.setDeepSearchLoading(false);
      this.setDone('searchDone', scroll);
    }
  }

  updateFilterCount(value, queryData, filterCountName) {
    const { deepSearch } = Store;
    let filterCount = deepSearch[filterCountName];
    let totalFilterCount = deepSearch.totalFilterCount;

    if (value && !queryData) {
      filterCount += 1;
      totalFilterCount += 1;
    } else if (!value && queryData) {
      filterCount -= 1;
      totalFilterCount -= 1;
    }

    this.setData({
      deepSearch: {
        ...deepSearch,
        [filterCountName]: filterCount,
        totalFilterCount
      }
    });
    this.setDirty(true);
  }
  // #endregion

  // #region getters
  get emptySearch() {
    return cloneDeep(initialFiltersValue);
  }

  get filters() {
    const { simpleFilterParams, lastSearchParameters } = Store;

    return lastSearchParameters.filters.length
      ? lastSearchParameters.filters
      : [simpleFilterParams];
  }

  get lastSearchParameters() {
    return Store.lastSearchParameters;
  }

  get searchParameters() {
    return Store.searchParameters;
  }

  get simpleFilterParams() {
    return Store.simpleFilterParams;
  }

  get isDirty() {
    return Store.isDirty;
  }

  get deepSearchObject() {
    return Store.deepSearch;
  }

  get errorFields() {
    return Store.errorFields;
  }
  // #endregion

  // #region api calls
  @action
  async getAllModulesTree() {
    try {
      return await Request('get.modules_tree');
    } catch (err) {
      UiService.handleError(err);

      return Promise.reject(err);
    }
  }

  @action
  async getAllQuizzesExercise() {
    try {
      return await Request('get.quizzes_exercise');
    } catch (err) {
      UiService.handleError(err);

      return Promise.reject(err);
    }
  }

  @action
  async getAllQuizzesProfile() {
    try {
      return await Request('get.quizzes_profile');
    } catch (err) {
      UiService.handleError(err);

      return Promise.reject(err);
    }
  }

  @action
  async getQuizProfiles(options) {
    try {
      return await Request('get.quiz_profiles', options);
    } catch (err) {
      UiService.handleError(err);

      return Promise.reject(err);
    }
  }
  // #endregion
}

export default new DeepSearchService();
